import { useEffect, useMemo } from 'react';
import useQueryScope from '../queries/useQueryScope';
import { useQueryClient } from '@tanstack/react-query';
import { newsFirstPageQuery } from '../queries/newsFeedQueries';
import { preloadImages } from '../utils/imageCache';
import { postImageUrls } from '../utils/postMedia';

export const newsPreviewImages = (posts = []) => [...new Set(
  posts.slice(0, 3).flatMap(post => postImageUrls(post?.media).slice(0, 2)),
)];

export const preloadNewsImages = (posts, fetchPriority = 'low') => preloadImages(newsPreviewImages(posts), {
  decoding: 'async', fetchPriority, concurrency: 2,
});

export default function useNewsImagePreload({ userId, communityId, token, posts }) {
  const queryClient = useQueryClient();
  const { scope, isCurrent, enabled } = useQueryScope({ user: { id: userId }, token, communityId });
  const urls = useMemo(() => newsPreviewImages(posts), [posts]);

  useEffect(() => {
    if (!enabled || !userId || !communityId || !token || !urls.length) return undefined;
    const preload = () => { if (!isCurrent()) return; void preloadImages(urls, { decoding: 'async', fetchPriority: 'low', concurrency: 2 }).catch(() => {}); };
    // Warm known photos while the browser is idle without competing with Home's hero.
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preload, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preload, 800);
    return () => window.clearTimeout(id);
  }, [enabled, isCurrent, userId, communityId, token, urls]);

  useEffect(() => {
    if (!enabled || !userId || !communityId || !token) return undefined;
    let active = true;
    const query = newsFirstPageQuery({ userId, communityId, token, scope });
    const prefetch = async () => {
      if (!active || !isCurrent()) return;
      try {
        const page = await queryClient.fetchQuery(query);
        if (active && isCurrent()) await preloadNewsImages(page.posts, 'auto');
      } catch {
        // Failed prefetches must never block navigation; the page can retry.
      }
    };
    window.__ls_prefetch = window.__ls_prefetch || {};
    window.__ls_prefetch.news = prefetch;
    return () => {
      active = false;
      if (window.__ls_prefetch?.news === prefetch) delete window.__ls_prefetch.news;
      void queryClient.cancelQueries({ queryKey: query.queryKey, exact: true });
      queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
    };
  }, [enabled, isCurrent, queryClient, scope, userId, communityId, token]);
}
